// ── WHAT A FAILED REQUEST SAYS ON SCREEN (2026-10-30) ────────────────────────────────────────────
//
// IDENTICAL COPY in dashboard/, portal/ and app/ (src/lib/plainError.ts). CI diffs them.
//
// Caleb, 2026-10-30, on `Couldn't take them off: API 400: {"detail":"Every list must be assigned to
// somebody or a group."}`: "it doesn't need to say an API error it needs to say what the actual
// problem is". Every api() helper used to build its error as `API <status>: <raw body>` and about
// 290 places on the three apps put that string straight on screen. Now the error's MESSAGE is the
// sentence for a person, built here, and the raw text moves to `raw` for logs and telemetry.
//
// The rule for what a person sees:
//   * the server's own sentence (`detail`) when it sent one -- the server knows what went wrong;
//   * otherwise a plain sentence for the kind of failure, never a status code, never JSON or HTML.
// docs/decisions/2026-10-30-errors-say-the-problem-in-plain-words.md

/** The sentence for a request that never got an answer: no connection, or ours ran out of time. */
export function unreachableText(timedOut: boolean): string {
  return timedOut
    ? "Orator took too long to answer. Check your internet and try again."
    : "Couldn't reach Orator. Check your internet and try again.";
}

/** First letter up, a full stop at the end -- server sentences are written both ways. */
function tidy(s: string): string {
  const t = s.trim().replace(/\s+/g, " ");
  if (!t) return t;
  const up = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?…)"']$/.test(up) ? up : `${up}.`;
}

/** The sentence a person sees for a failed request with this status and body. */
export function plainError(status: number, body: string): string {
  let detail: unknown;
  try { detail = JSON.parse(body)?.detail; } catch { detail = undefined; }
  if (typeof detail === "string" && detail.trim()) return tidy(detail);
  // A refusal that carries more than words keeps its words in `message` (0405's "they wrote again").
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const m = (detail as { message?: unknown }).message;
    if (typeof m === "string" && m.trim()) return tidy(m);
  }
  // FastAPI's own validation failure: a list of field complaints written for programmers.
  if (Array.isArray(detail)) return "Something on this form isn't filled in right. Check it and try again.";
  if (status === 401) return "Your sign-in has expired. Sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That couldn't be found. It may have been removed or archived.";
  if (status === 409) return "That changed while you were looking at it. Refresh the page and try again.";
  if (status === 413) return "That file is too big to upload.";
  if (status === 429) return "Too many tries at once. Wait a moment and try again.";
  if (status >= 500) return "Something went wrong on Orator's end. Nothing was changed. Try again in a minute.";
  return "That didn't work. Try again.";
}

/** Is trying the same thing again likely to work? Only when the failure was the connection or the
 *  server -- a refusal ("only a manager can do that") fails the same way every time. */
export function canRetry(e: unknown): boolean {
  const s = (e as { status?: unknown })?.status;
  return typeof s === "number" && (s === 0 || s === 429 || s >= 500);
}
